import { GradientText } from '@medeloop/medeloop-components-library';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MaterialSymbol } from 'react-material-symbols';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getCodesetsByOrganizationAndDatasource } from 'src/api/codesets';
import { useCreateCohortMutation } from 'src/api/create-cohort/mutation';
import { useModal } from 'src/hooks/modal-provider';
import {
  allCodesetsReceived,
  cohortCreated,
  cohortPageExited,
  selectCohortState,
  selectDataSourceId,
  selectRootOrganizationId,
  startCohortStream,
  stopCohortStream,
  userQuerySent,
} from 'src/store/create-cohort';
import { showToast } from 'src/store/toast';
import { CohortState, CohortStepType } from 'src/types/cohorts';
import { to } from 'to-await';
import { ChatInput } from './chat-input';
import { ChatMessages } from './chat-messages';
import { CohortTitle } from './cohort-title';

export const CreateCohortPage = () => {
  const { mutateAsync: createCohort } = useCreateCohortMutation();
  const cohortState: CohortState = useSelector(selectCohortState);
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const originSearchParam: string | null = searchParams.get('origin');
  const originHref = searchParams.get('originHref');

  const dataSourceId = useSelector(selectDataSourceId);
  const organizationId = useSelector(selectRootOrganizationId);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { clearAllModals } = useModal();

  const hasTransitioned = cohortState.userQuery && cohortState.currentStep;

  useEffect(() => {
    if (cohortState.cohortId !== null) {
      dispatch(startCohortStream({ pk: cohortState.dataSourceId }));
    }

    return () => {
      dispatch(cohortPageExited());
      dispatch(stopCohortStream());
      clearAllModals();
    };
  }, []);

  const handleNavigateBack = () => {
    if (originSearchParam === 'rwa' && originHref) {
      window.location.replace(originHref);
      return;
    }
    navigate(-1);
  };

  const onSendMessage = async (message: string) => {
    dispatch(
      userQuerySent({
        userQuery: message,
        currentStep: CohortStepType.QueryValidation,
      }),
    );

    const [error, response] = await to(
      createCohort({ dataSourceId: dataSourceId!, query: message }),
    );

    if (error) {
      dispatch(showToast({ message: error.message, type: 'dangerous' }));
      return;
    }

    const fetchCodesets = async () => {
      if (organizationId && dataSourceId) {
        const [error, response] = await to(
          getCodesetsByOrganizationAndDatasource(organizationId, dataSourceId),
        );
        if (error) {
          dispatch(showToast({ message: error.message, type: 'dangerous' }));
          return;
        }
        dispatch(allCodesetsReceived(response.codesets));
      }
    };

    fetchCodesets();

    dispatch(cohortCreated(response.data));
    dispatch(
      startCohortStream({
        pk: response.data.dataSourceId,
        sk: response.data.cohortId,
      }),
    );
  };

  return (
    <div className="w-full h-full bg-background-dark-default flex flex-col">
      {/* Header */}
      <div className="w-full grid grid-cols-[max-content_1fr] bg-background-dark-subdued border-b-stroke-on-dark-subdued border-b-[1px]">
        <div>
          <button
            className="flex items-center gap-2 p-4 text-content-on-dark-default hover:text-content-on-dark-subdued"
            onClick={handleNavigateBack}
          >
            <MaterialSymbol icon="arrow_back" size={16} />
            <span className="text-md font-medium"> {t('cohort')}s </span>
          </button>
        </div>
        <div className="flex items-center justify-center">
          {cohortState.name && <CohortTitle title={cohortState.name} />}
        </div>
      </div>

      {/* Main Container */}
      <div
        className={`flex flex-col flex-1 min-h-0 items-center ${
          hasTransitioned ? 'justify-between' : 'justify-center'
        } p-4`}
      >
        <motion.div
          className={`w-3/4 overflow-y-scroll pb-6`}
          initial={{ opacity: 0, y: 20 }}
          animate={{
            opacity: 1,
            y: 0,
          }}
          transition={{
            duration: 0.6,
            ease: [0.16, 1, 0.3, 1],
          }}
        >
          <AnimatePresence mode="wait">
            {hasTransitioned ? (
              <motion.div
                key="chat-interface"
                className="flex flex-col"
                initial={{ opacity: 0, height: 0 }}
                animate={{
                  opacity: 1,
                  height: '100%',
                }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.5 }}
              >
                <ChatMessages cohortState={cohortState} />
              </motion.div>
            ) : (
              <motion.div
                key="welcome-prompt"
                className="flex flex-col text-center"
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                transition={{
                  duration: 0.5,
                  delay: 0.2,
                  ease: 'easeOut',
                }}
              >
                <GradientText text="What cohort do you want to create today?" />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        <motion.div
          className="w-3/4"
          initial={{ opacity: 0 }}
          animate={{
            opacity: 1,
          }}
          transition={{
            duration: 0.4,
            delay: 0.4,
          }}
        >
          <ChatInput onSendMessage={onSendMessage} disabled={!!hasTransitioned} />
        </motion.div>
      </div>
    </div>
  );
};
